import { useCallback, useEffect, useState } from 'react';
import { NotificationService } from '../../../Components/Notifications';
import { usePropertiesList } from './use-properties-list';
import { propertiesApi } from './properties-api';
import {
  API_MODE,
  PROPERTIES_API_ERROR_MESSAGES,
  PROPERTIES_API_SUCCESS_MESSAGES,
  PROPERTIES_DIALOG_MODES,
  PROPERTY_ID,
} from './constants';

export const useProperties = () => {
  const [propertiesDialogMode, setPropertiesDialogMode] = useState(null);
  const [property, setProperty] = useState({});
  const [loadingMode, setLoadingMode] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const {
    fetchPropertiesList,
    propertiesList,
    selectedPropertyId,
    setSelectedPropertyId,
  } = usePropertiesList({ loadingMode, setLoadingMode, setErrorMessage });

  const fetchPropertyById = useCallback(
    async (propertyId) => {
      setLoadingMode(API_MODE.PROPERTY);
      try {
        const property = await propertiesApi.getPropertyById(propertyId);
        setProperty(property);
      } catch {
        setErrorMessage({
          [API_MODE.PROPERTY]: PROPERTIES_API_ERROR_MESSAGES.GET_PROPERTY,
        });
      }
      setLoadingMode(null);
    },
    [setLoadingMode],
  );

  const updatePropertyTabData = (propertyTabData = {}) => {
    setProperty((prevState) => ({
      ...prevState,
      ...propertyTabData,
    }));
  };

  const updateProperty = async (propertyUpdates, propertyTabData) => {
    setLoadingMode(API_MODE.PROPERTY_UPDATE);
    try {
      await propertiesApi.updateProperty(
        property[PROPERTY_ID],
        propertyUpdates,
      );
      updatePropertyTabData(propertyTabData);
    } catch {
      setErrorMessage({
        [API_MODE.PROPERTY_UPDATE]:
          PROPERTIES_API_ERROR_MESSAGES.UPDATE_PROPERTY,
      });
    }
    setLoadingMode(null);
  };

  useEffect(() => {
    if (selectedPropertyId) {
      fetchPropertyById(selectedPropertyId);
    }
  }, [fetchPropertyById, selectedPropertyId]);

  const closePropertiesDialog = () => {
    setPropertiesDialogMode(null);
  };

  const handleSetPropertiesDialogMode = (mode) => () => {
    setPropertiesDialogMode(mode);
  };

  const closeErrorDialog = () => {
    setErrorMessage(null);
  };

  const createProperty = async (newPropertyData) => {
    setLoadingMode(API_MODE.PROPERTY_CREATE);
    try {
      await propertiesApi.createProperty(newPropertyData);

      closePropertiesDialog();
      await fetchPropertiesList();

      NotificationService.notify(
        PROPERTIES_API_SUCCESS_MESSAGES.CREATE_PROPERTY,
      );
    } catch {
      setErrorMessage({
        [API_MODE.PROPERTY_CREATE]:
          PROPERTIES_API_ERROR_MESSAGES.CREATE_PROPERTY,
      });
    }
    setLoadingMode(null);
  };

  const transferProperty = () =>
    // TODO: add parameters for transfer property when API is implemented
    // leasingTeamPublicId,
    // defaultManagerOwnerUsername,
    {
      closePropertiesDialog();
    };

  const {
    [API_MODE.PROPERTY]: propertyErrorMessage,
    [API_MODE.PROPERTY_LIST]: propertiesListErrorMessage,
    [API_MODE.PROPERTY_CREATE]: propertyCreateErrorMessage,
    [API_MODE.PROPERTY_UPDATE]: propertyUpdateErrorMessage,
  } = errorMessage || {};

  const propertiesDialogConfig = {
    [PROPERTIES_DIALOG_MODES.CREATE]: {
      isOverlayOpen: loadingMode === API_MODE.PROPERTY_CREATE,
      primaryAction: createProperty,
    },
    [PROPERTIES_DIALOG_MODES.TRANSFER]: {
      isOverlayOpen: loadingMode === API_MODE.PROPERTY_TRANSFER,
      primaryAction: transferProperty,
    },
  };

  return {
    closeErrorDialog,
    closePropertiesDialog,
    handleSetPropertiesDialogMode,
    isLoadingProperty: loadingMode === API_MODE.PROPERTY,
    isLoadingPropertiesList: loadingMode === API_MODE.PROPERTY_LIST,
    isUpdatingProperty: loadingMode === API_MODE.PROPERTY_UPDATE,
    propertiesDialogConfig,
    propertiesDialogMode,
    propertiesList,
    propertiesListErrorMessage,
    property,
    propertyErrorMessage,
    propertyUpdateErrorMessage,
    propertyCreateErrorMessage,
    selectedPropertyId,
    setSelectedPropertyId,
    updateProperty,
  };
};
